import Divider from '@mui/material/Divider';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';

import ResponseLengthTab from '@webclient/components/response-length-tab';
import KnowledgeSourceIcon from '@webclient/components/search-forms/gamification-search/actions/scope-selector/knowledge-source-icon';
import SearchModeTab from '@webclient/components/search-mode-tab';
import FormHeader from '@webclient/components/settings/components/form-header';
import CustomResponseStyle from '@webclient/components/settings/components/tabs/search/components/custom-response-style';
import ModeSelector from '@webclient/components/settings/components/tabs/search/components/mode-selector';
import FieldGroup from '@webclient/components/settings/components/tabs/search/components/row';
import {
  StyledFormControlLabel,
  StyledRadio,
  StyledRadioLabel,
} from '@webclient/components/settings/components/tabs/search/style';
import UserAccountActionCard from '@webclient/components/settings/components/user-account-action-card';
import { StyledScrollableTabContent } from '@webclient/components/settings/styles';
import Switch from '@webclient/components/switch';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useGetCredit from '@webclient/hooks/useGetCredit';
import useResponsive from '@webclient/hooks/useResponsive';

import {
  setSearchScopeByUserId,
  setUploadedToKnowledgeBaseByUserId,
  setUseCustomStyleByUserId,
} from '@webclient/state/slices/app-settings';
import {
  selectSearchScope,
  selectShouldSaveUploadsToKnowledgeBase,
  selectUseCustomStyle,
} from '@webclient/state/slices/app-settings/userSettingSelector';

import { SearchScopeType } from '@webclient/shared-types/search-result/types';

import ArrowLeftIcon from '~icons/knowz-iconify/arrow-left';
import DownloadFileRoundedIcon from '~icons/knowz-iconify/download-file-rounded';
import EditIcon from '~icons/knowz-iconify/edit-squared';
import SearchIcon from '~icons/knowz-iconify/search-smart';
import UploadIcon from '~icons/knowz-iconify/upload-rounded';

import { Checkbox, IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

type Props = {
  onClose: (event: React.SyntheticEvent) => void;
};

export default function Search({ onClose }: Props) {
  const isItDesktop = useResponsive('up', 'md');
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { getAccountType } = useGetCredit();
  const searchScope = useAppSelector(selectSearchScope);
  const me = useAppSelector((state) => state.auth.me);
  const useCustomStyle = useAppSelector(selectUseCustomStyle);
  const shouldSaveUploadsToKnowledgeBase = useAppSelector(
    selectShouldSaveUploadsToKnowledgeBase,
  );
  function setSearchScope(
    _e: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) {
    if (!me?.uuid) return;
    dispatch(
      setSearchScopeByUserId({
        userId: me.uuid,
        searchScope: value as SearchScopeType,
      }),
    );
  }

  function handlesetUploadedKnowledgeBaseSwitcher(
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) {
    if (!me) return;
    dispatch(
      setUploadedToKnowledgeBaseByUserId({
        userId: me.uuid,
        shouldSave: checked,
      }),
    );
  }

  function toggleUseCustomStyle(
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) {
    if (!me?.uuid) return;
    dispatch(
      setUseCustomStyleByUserId({
        userId: me.uuid,
        useCustomStyle: checked,
      }),
    );
  }

  return (
    <>
      <FormHeader
        title={t('layout.main.sections.header.settings.tabs.search', 'Search')}
      >
        {!isItDesktop && (
          <IconButton
            color="secondary"
            variant="ghost"
            onClick={onClose}
          >
            <ArrowLeftIcon />
          </IconButton>
        )}
      </FormHeader>

      <StyledScrollableTabContent>
        <UserAccountActionCard
          onClose={onClose}
          type="search"
          accountType={getAccountType()}
        />

        <FieldGroup
          icon={<SearchIcon color="grey" />}
          title={t(
            'layout.main.sections.header.settings.search.defaultSearchMode',
            'Default search personality',
          )}
        >
          <ModeSelector />
        </FieldGroup>

        <Divider />

        <FieldGroup
          icon={<UploadIcon color="grey" />}
          title={t(
            'layout.main.sections.header.settings.search.saveUploadToKnoweledgeBase.title',
            'Save Uploads to Knowledge Base',
          )}
          description={t(
            'layout.main.sections.header.settings.search.saveUploadToKnoweledgeBase.description',
            'Save files uploaded automatically to the Knowledge Base. File uploads not available in Fast search mode.',
          )}
        >
          <Switch
            checked={shouldSaveUploadsToKnowledgeBase}
            onChange={handlesetUploadedKnowledgeBaseSwitcher}
          />
        </FieldGroup>

        <Divider />

        {/* //TODO:Enable once auto-play is implemented
        <Row
          icon={
            <StyledIconContainer>
              <PlayIcon color="grey" />
            </StyledIconContainer>
          }
          title="Playback automatically on search"
          description="Automatically speak aloud search results"
        >
          <Switch
            checked={shouldPlayAudioAutomatically}
            onChange={() =>
              handleSetPlayAudioAutomatically(!shouldPlayAudioAutomatically)
            }
          />
        </Row>

        <Divider /> */}

        <FieldGroup
          direction="column"
          icon={<DownloadFileRoundedIcon color="grey" />}
          title={t(
            'layout.searchWidget.filters.responseLength.title',
            'Response Length',
          )}
          description={t(
            'layout.searchWidget.filters.responseLength.subtitle',
            'Select your preferred response length below and adjust anytime. Actual length may vary based on your input.',
          )}
        >
          <ResponseLengthTab />
        </FieldGroup>

        <Divider />

        <FieldGroup
          direction="column"
          icon={<DownloadFileRoundedIcon color="grey" />}
          title={t(
            'layout.searchWidget.filters.Thoughtfulness.title',
            'Thoughtfulness',
          )}
          description={t(
            'layout.searchWidget.filters.Thoughtfulness.subtitle',
            'How long should Knowz think before answering? longer insights use more credits, Genius mode will perform multi-step deep research.',
          )}
        >
          <SearchModeTab />
        </FieldGroup>

        {/* //TODO: we add this back once it is handled in AI server 
        {searchMode === 'advanced' && <ResearchTime />}
        */}

        <Divider />

        <FieldGroup
          icon={<EditIcon color="grey" />}
          title={t(
            'layout.main.sections.header.settings.search.customizeResponse.title',
            'Customize response style?',
          )}
          description={t(
            'layout.main.sections.header.settings.search.customizeResponse.description',
            'You can customize how Knowz responds - whether you prefer short and concise replies, rhyming answers, informal tone, emoji-filled messages, or something entirely unique.',
          )}
        >
          <Checkbox
            checked={useCustomStyle}
            onChange={toggleUseCustomStyle}
          />
        </FieldGroup>

        <Stack gap={3}>
          <CustomResponseStyle />
        </Stack>

        <Divider />

        <Stack gap={3}>
          <FieldGroup
            title={t(
              'layout.main.sections.header.settings.search.whereToSearch.title',
              'Where to search?',
            )}
            description={t(
              'layout.main.sections.header.settings.search.whereToSearch.subtitle',
              'Select if Knowz searches just the web, your workspace Knowledge Base or both for answers.',
            )}
          />

          <RadioGroup
            sx={{ gap: 2 }}
            value={searchScope}
            onChange={setSearchScope}
          >
            <StyledFormControlLabel
              value="web_kb"
              labelPlacement="start"
              control={<StyledRadio />}
              label={
                <StyledRadioLabel>
                  <KnowledgeSourceIcon mode="web_kb" />
                  <Text variant="textSm">Web & Knowledge Base</Text>
                </StyledRadioLabel>
              }
            />

            <StyledFormControlLabel
              value="kb_only"
              labelPlacement="start"
              control={<StyledRadio />}
              label={
                <StyledRadioLabel>
                  <KnowledgeSourceIcon mode="kb_only" />
                  <Text variant="textSm">Knowledge Base Only</Text>
                </StyledRadioLabel>
              }
            />

            <StyledFormControlLabel
              value="web_only"
              labelPlacement="start"
              control={<StyledRadio />}
              label={
                <StyledRadioLabel>
                  <KnowledgeSourceIcon mode="web_only" />
                  <Text variant="textSm">Web Only</Text>
                </StyledRadioLabel>
              }
            />
          </RadioGroup>
        </Stack>
      </StyledScrollableTabContent>
    </>
  );
}
